import { Linkedin, Lock, Mail, MapPin, Phone } from "lucide-react";
import { Reveal, PageHero } from "@/components/Shared";
import { contact } from "@/data/content";

const rows = [
    {
        label: "Phone",
        value: contact.phone,
        href: contact.phoneHref,
        icon: Phone,
        testId: "contact-phone",
    },
    {
        label: "Email",
        value: contact.email,
        href: `mailto:${contact.email}`,
        icon: Mail,
        testId: "contact-email",
    },
    {
        label: "LinkedIn",
        value: "Connect with Infrasic Intelligence",
        href: contact.linkedin,
        icon: Linkedin,
        testId: "contact-linkedin",
        external: true,
    },
];

export default function Contact() {
    return (
        <div data-testid="contact-page">
            <PageHero
                index="06"
                overline="Contact"
                title="Start with the question."
                lead="Every investigation begins with understanding the situation, the available evidence and the questions that need to be answered. Speak with us if you are dealing with suspected fraud, a whistleblower complaint, employee or management misconduct, questionable or potentially altered bank statements, unexplained financial transactions, procurement or vendor concerns, conflicts of interest, fund diversion, digital evidence, data theft, asset tracing, due diligence concerns or another complex investigative matter."
                testId="contact-hero"
            />

            <section className="blueprint-grid relative overflow-hidden">
                <div className="ambient-glow -right-40 -top-24 h-[420px] w-[420px] bg-[#1D4F9C]/22" aria-hidden />
                <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28">
                <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr]">
                    {/* Firm dossier */}
                    <Reveal>
                        <div className="certificate h-full p-10 lg:p-14" data-testid="founder-dossier">
                            <p className="font-tech text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D9A84E]">
                                Contact
                            </p>
                            <h2 className="mt-5 font-display text-2xl font-semibold text-[#E8ECF4] sm:text-3xl">
                                Connect with Infrasic Intelligence
                            </h2>

                            <div className="mt-10 border border-[#243650] bg-[#0B1322] p-8 text-center">
                                <img
                                    src={contact.image}
                                    alt={`${contact.founder} portrait`}
                                    className="mx-auto h-28 w-28 rounded-full border-2 border-[#D9A84E]/80 object-cover bg-[#F2EEE4] shadow-[0_0_0_6px_rgba(217,168,78,0.12)]"
                                />
                                <p className="mt-6 font-tech text-[10px] uppercase tracking-[0.24em] text-[#8C97AC]">
                                    {contact.firm}
                                </p>
                                <p className="mt-4 font-display text-2xl font-semibold text-[#E8ECF4]" data-testid="founder-name">
                                    {contact.founder}
                                </p>
                                <p className="mt-1.5 text-sm font-medium text-[#5B93E8]">{contact.role}</p>
                                <p className="mt-1.5 font-tech text-xs tracking-wide text-[#8C97AC]" data-testid="founder-credentials">
                                    {contact.credentials}
                                </p>
                            </div>

                            <div className="mt-8">
                                <a
                                    href={`mailto:${contact.email}?subject=Confidential%20Consultation%20Request`}
                                    data-testid="consultation-cta"
                                    className="group inline-flex items-center gap-3 bg-[#2E62B0] px-7 py-4 font-tech text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-[#3D74C6]"
                                >
                                    Request a Confidential Consultation
                                </a>
                            </div>

                            <div className="mt-8 flex items-start gap-3 border-t border-[#243650] pt-8" data-testid="confidentiality-note">
                                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[#D9A84E]" />
                                <p className="text-xs leading-relaxed text-[#8C97AC]">
                                    All enquiries are handled with strict confidentiality, discretion
                                    and professional independence. Sensitive matters may be discussed
                                    directly by telephone.
                                </p>
                            </div>
                        </div>
                    </Reveal>

                    {/* Contact directory */}
                    <Reveal delay={0.12}>
                        <div className="flex h-full flex-col border border-[#243650] bg-[#0F1B2E]">
                            <p className="border-b border-[#243650] px-8 py-4 font-tech text-[10px] font-semibold uppercase tracking-[0.3em] text-[#5B93E8]">
                                Direct Lines
                            </p>
                            <div className="flex-1">
                                {rows.map((row) => (
                                    <a
                                        key={row.label}
                                        href={row.href}
                                        {...(row.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                                        data-testid={row.testId}
                                        className="group flex items-center gap-6 border-b border-[#243650] px-8 py-7 transition-colors duration-300 hover:bg-[#0B1322]"
                                    >
                                        <span className="border border-[#5B93E8]/30 bg-[#16263F] p-3">
                                            <row.icon className="h-5 w-5 text-[#5B93E8]" />
                                        </span>
                                        <span>
                                            <span className="block font-tech text-[10px] uppercase tracking-[0.26em] text-[#8C97AC]">
                                                {row.label}
                                            </span>
                                            <span className="mt-1.5 block text-[15px] font-medium text-[#E8ECF4] transition-colors duration-300 group-hover:text-[#5B93E8]">
                                                {row.value}
                                            </span>
                                        </span>
                                    </a>
                                ))}
                                <div className="flex items-start gap-6 px-8 py-7" data-testid="contact-address">
                                    <span className="border border-[#5B93E8]/30 bg-[#16263F] p-3">
                                        <MapPin className="h-5 w-5 text-[#5B93E8]" />
                                    </span>
                                    <span>
                                        <span className="block font-tech text-[10px] uppercase tracking-[0.26em] text-[#8C97AC]">
                                            Registered Office
                                        </span>
                                        <span className="mt-1.5 block max-w-sm text-[15px] font-medium leading-relaxed text-[#E8ECF4]">
                                            {contact.address}
                                        </span>
                                    </span>
                                </div>
                            </div>
                            <div className="border-t border-[#243650] bg-[#0B1322] px-8 py-5">
                                <p className="font-tech text-[10px] uppercase tracking-[0.2em] text-[#8C97AC]" data-testid="contact-registrations">
                                    DPIIT Startup Recognition: <span className="text-[#5B93E8]">{contact.dpiit}</span>
                                    <span className="mx-2 text-[#243650]">|</span>
                                    Udyam: <span className="text-[#5B93E8]">{contact.udyam}</span>
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
                </div>
            </section>
        </div>
    );
}
